import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Plane, Hotel, Car, Package, Ticket, Calendar, XCircle, Search } from "lucide-react";
import { api, money } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { toast } from "sonner";

const ICON = { flight: Plane, hotel: Hotel, car: Car, package: Package };
const STATUS = {
  confirmed: "bg-emerald-50 text-emerald-700", pending: "bg-amber-50 text-amber-700",
  cancelled: "bg-red-50 text-red-600", completed: "bg-blue-50 text-blue-700",
};

export default function MyBookings() {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = () => api.get("/bookings/me").then(({ data }) => setBookings(data.results)).finally(() => setLoading(false));

  useEffect(() => {
    if (authLoading) return;
    if (!user) { navigate("/login?next=/my-bookings"); return; }
    load();
  }, [user, authLoading]);

  const cancel = async (id) => {
    try { await api.post(`/bookings/${id}/cancel`); toast.success("Booking cancelled"); load(); }
    catch { toast.error("Could not cancel"); }
  };

  if (authLoading || !user) return null;

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 py-10">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="font-heading font-black text-3xl text-slate-950">My bookings</h1>
          <p className="text-slate-500 mt-1">Manage all your trips in one place, {user.name?.split(" ")[0]}.</p>
        </div>
        <Button onClick={() => navigate("/")} className="rounded-full bg-brand hidden sm:flex"><Search className="w-4 h-4" />Book a new trip</Button>
      </div>

      {loading ? <div className="space-y-4">{Array.from({ length: 3 }).map((_, i) => <Skeleton key={i} className="h-32 rounded-2xl" />)}</div> :
        bookings.length === 0 ? (
          <div className="bg-white border border-slate-200 rounded-2xl p-16 text-center">
            <Ticket className="w-14 h-14 text-slate-300 mx-auto mb-4" />
            <h2 className="font-heading font-bold text-xl text-slate-950">No bookings yet</h2>
            <p className="text-slate-500 mt-2">Your future adventures will appear here.</p>
            <Link to="/"><Button className="mt-6 rounded-full bg-coral hover:bg-coral-hover">Start exploring</Button></Link>
          </div>
        ) : (
          <div className="space-y-4">
            {bookings.map((b, i) => {
              const Icon = ICON[b.type] || Package;
              return (
                <motion.div key={b.id} initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.05 }} data-testid={`booking-${i}`}
                  className="bg-white border border-slate-200 rounded-2xl p-6 flex flex-col sm:flex-row gap-5 sm:items-center">
                  <div className="w-12 h-12 rounded-xl bg-brand/10 flex items-center justify-center shrink-0"><Icon className="w-6 h-6 text-brand" /></div>
                  <div className="flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <h3 className="font-heading font-bold text-lg text-slate-950">{b.title}</h3>
                      <span className={`text-xs font-bold px-2.5 py-1 rounded-full capitalize ${STATUS[b.status]}`}>{b.status}</span>
                    </div>
                    <div className="flex items-center gap-4 text-sm text-slate-500 mt-1">
                      <span className="font-mono font-bold text-slate-700">{b.reference}</span>
                      {b.travel_date && <span className="flex items-center gap-1"><Calendar className="w-3.5 h-3.5" />{b.travel_date}</span>}
                    </div>
                  </div>
                  <div className="flex items-center gap-4 sm:flex-col sm:items-end">
                    <div className="font-heading font-black text-xl text-slate-950">{money(b.amount, b.currency)}</div>
                    {b.status !== "cancelled" && (
                      <button onClick={() => cancel(b.id)} data-testid={`cancel-${i}`} className="text-red-600 text-sm font-bold flex items-center gap-1 hover:underline"><XCircle className="w-4 h-4" />Cancel</button>
                    )}
                  </div>
                </motion.div>
              );
            })}
          </div>
        )}
    </div>
  );
}
